<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
// Overrides @basecom-gmbh/pond/components/Account/AccountLogin.vue
import { ApiClientError } from '@shopware/api-client';
import type { ResolvedApiError } from '~/types/errors';
import { useCustomFormkitHelper } from '../../composables/useCustomFormkitHelper';

type FormkitLoginFields = {
    username: string;
    password: string;
};

const props = withDefaults(
    defineProps<{
        redirectAfterSuccess?: boolean;
        redirectTarget?: string;
        showCreateLink?: boolean;
        showRecoverLink?: boolean;
    }>(),
    {
        redirectAfterSuccess: false,
        redirectTarget: '/account',
        showCreateLink: true,
        showRecoverLink: true,
    },
);

defineEmits(['closeModal']);

const customerStore = useCustomerStore();
const { resolveApiErrors } = useApiErrorsResolver();
const { errorOfField } = useFormkitHelper();
const { signedIn } = storeToRefs(customerStore);
const apiErrors = ref<ResolvedApiError[]>([]);
const formErrorStore = useFormErrorStore();
const { pushError, pushSuccess } = useNotifications();
const { mergeWishlistProducts } = useWishlist();
const { trackLogin } = useAnalytics();
const { t } = useI18n();
const { togglePasswordField } = useCustomFormkitHelper();
const { sessionContext } = useSessionContext();
const countryCookie = useCookie('country', { maxAge: 92275200 });
const { countryConfig } = useCountrySwitch();
const route = useRoute();
const { cartItems, refreshCart } = useCart();
const { updateChangedLineItemsOnLogin } = useCartItemsStore();

const handleLogin = async (fields: FormkitLoginFields) => {
    let cartBefore = null;
    const isCheckoutConfirm = route.path === '/checkout/confirm';
    if (isCheckoutConfirm) {
        cartBefore = cartItems.value;
    }
    try {
        await customerStore.login({
            ...fields,
        });

        // Get customer country from active billing address or default to 'com' and redirect
        // to the appropriate country URL based on the countryConfig.
        let customerCountryIso =
            sessionContext.value.customer?.activeBillingAddress?.country?.iso?.toLowerCase() ?? '';

        if (countryConfig[customerCountryIso as string] === undefined) {
            customerCountryIso = 'com';
        }

        const isUserFromSameCountry = customerCountryIso === countryCookie.value;
        countryCookie.value = customerCountryIso;

        // Check for redirect query param and redirect if present
        const redirectPath = route.query.redirect as string | undefined;
        if (isUserFromSameCountry && redirectPath) {
            window.location.replace(`${countryConfig[countryCookie.value].prefix}${redirectPath}`);
        }

        if ((!redirectPath || !isUserFromSameCountry) && props.redirectAfterSuccess) {
            window.location.replace(`${countryConfig[countryCookie.value].prefix}${props.redirectTarget}`);
        }
        mergeWishlistProducts();
        trackLogin();
        pushSuccess(t('account.login.successMessage'));

        if (isCheckoutConfirm) {
            await refreshCart();
            const cartAfter = cartItems.value;
            const beforeItems = new Map(cartBefore?.map(item => [item.id, item.quantity]));
            const newProducts = (cartAfter || []).filter(item => {
                const previousQuantity = beforeItems.get(item.id);
                return !previousQuantity || previousQuantity !== item.quantity;
            });
            if (newProducts.length) {
                updateChangedLineItemsOnLogin(newProducts);
            }
        }
    } catch (error) {
        pushError(t('account.login.errorMessage'));

        if (error instanceof ApiClientError) {
            const resolvedErrors = resolveApiErrors(error.details.errors, 'login');

            resolvedErrors.forEach(err => {
                if (
                    err.code === 'CHECKOUT__CUSTOMER_NOT_FOUND' ||
                    err.code === 'CHECKOUT__CUSTOMER_AUTH_BAD_CREDENTIALS'
                ) {
                    err.code = t('account.login.invalidCredentials');
                }
            });

            apiErrors.value = resolvedErrors;
            return;
        }

        apiErrors.value.push({ key: 'login', code: t('account.login.invalidCredentials') });
    }
};
</script>

<!--CUSTOM IDS-104: Changed styling-->
<template>
    
<FormKitLazyProvider config-file="true">
<FormKit
        v-if="!signedIn"
        type="form"
        :submit-label="t('account.login.submitLabel')"
        :classes="{
            form: 'w-full flex flex-wrap flex-col gap-4',
            actions: 'w-full',
        }"
        @submit="handleLogin"
    >
        <ul
            v-if="apiErrors.length"
            class="validation-errors text-status-danger"
        >
            <li
                v-for="(error, index) in apiErrors"
                :key="index"
            >
                {{ error.code }}
            </li>
        </ul>

        <FormKit
            type="email"
            :label="t('account.login.email.label')"
            name="username"
            :placeholder="t('account.login.email.placeholder')"
            :validation="[['required'], ['email']]"
            validation-visibility="blur"
            :validation-messages="{
                email: t('account.register.email.error'),
            }"
        />

        <FormKit
            type="password"
            :label="t('account.login.password.label')"
            name="password"
            :placeholder="t('account.login.password.placeholder')"
            :errors="errorOfField('password', formErrorStore.apiErrors)"
            :validation="[['required'], ['matches', /^[^\s]+$/u]]"
            validation-visibility="blur"
            suffix-icon="eye-strike-through"
            @suffix-icon-click="togglePasswordField"
        />
    </FormKit>

    <div
        v-if="!signedIn"
        class="mt-2 flex justify-between"
    >
        <LocaleLink
            v-if="showRecoverLink"
            to="/account/recover"
            class="underline underline-offset-2 hover:text-brand-primary"
            :class="{ 'text-right': showCreateLink }"
            @click="$emit('closeModal')"
        >
            {{ t('account.login.recoverPasswordLink') }}
        </LocaleLink>
    </div>
</FormKitLazyProvider>

</template>
